import { useState, useEffect } from "react";
import {
    Activity, Server, Power, Network, ShieldCheck, 
    AlertTriangle, AlertCircle, Clock, Zap, Cpu,
    Globe, CheckCircle2, HelpCircle, Link2, Share2,
    HardDrive, BarChart3
} from "lucide-react";
import baseapi from "../../../services/baseapi"; // Adjust import path
import { showError } from "../../../utils/toast"; // Adjust import path

interface IloData {
    id: number;
    asset_id: number;
    ilo_generation: number | null;
    ilo_firmware: string | null;
    ilo_manager_type: string | null;
    ilo_uuid: string | null;
    ilo_network_interfaces: string | null; // JSON string
    ilo_dedicated_network_ports: string | null; // JSON string
    ilo_shared_network_ports: string | null; // JSON string
    overall_health_status: string | null;
    critical_issues_count: number | null;
    warnings_count: number | null;
    total_issues_count: number | null;
    power_state: string | null;
    post_state: string | null;
    uptime_seconds: number | null;
    power_on_minutes: number | null;
    power_regulator_mode: string | null;
    power_consumed_watts: number | null;
    power_average_consumed_watts: number | null;
    power_capacity_watts: number | null;
    tpm_info: string | null; // JSON string
    target_host: string | null;
    collection_started_at: string | null;
    report_generated_at: string | null;
    schema_version: string | null;
    // New fields
    boot_source_override_target: string | null;
    boot_source_override_enabled: string | null;
    boot_source_override_mode: string | null;
    uefi_target_boot_source: string | null;
    cpu_util_pct: number | null;
    io_bus_util_pct: number | null;
    memory_bus_util_pct: number | null;
}

interface AssetIloProps {
    assetId: string | number;
}

export default function AssetIlo({ assetId }: AssetIloProps) {
    const [data, setData] = useState<IloData | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (assetId) {
            fetchIloData();
        }
    }, [assetId]);

    const fetchIloData = async () => {
        setLoading(true);
        try {
            const response = await baseapi.get("/asset_ilo", { params: { asset_id: assetId } });
            
            if (response.data.status && response.data.data && response.data.data.length > 0) {
                setData(response.data.data[0]);
            } else {
                setData(null);
            }
        } catch (error: any) {
            showError(error.message || "Failed to fetch iLO data");
            setData(null);
        } finally {
            setLoading(false);
        }
    };

    // Helper to safely parse JSON strings from the DB
    const safeParseJSON = (jsonStr: string | null, fallback: any = []) => {
        if (!jsonStr) return fallback;
        try {
            return JSON.parse(jsonStr);
        } catch (e) {
            return fallback;
        }
    };

    // Helper to format uptime
    const formatUptime = (seconds: number | null) => {
        if (!seconds) return "-";
        const days = Math.floor(seconds / (3600 * 24));
        const hours = Math.floor((seconds % (3600 * 24)) / 3600);
        return `${days}d ${hours}h`;
    };

    // Helper to get health colors
    const getHealthStyles = (status: string | null) => {
        const s = (status || "").toUpperCase();
        if (s === "CRITICAL") return "text-rose-700 dark:text-red-400 bg-rose-50 dark:bg-red-500/10 border-rose-200 dark:border-red-500/30";
        if (s === "WARNING") return "text-amber-700 dark:text-yellow-400 bg-amber-50 dark:bg-yellow-500/10 border-amber-200 dark:border-yellow-500/30";
        if (s === "OK" || s === "GOOD") return "text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-500/10 border-emerald-200 dark:border-emerald-500/30";
        return "text-slate-700 dark:text-gray-200 bg-slate-100 dark:bg-gray-800 border-slate-200 dark:border-gray-700";
    };

    // Render an individual network interface card
    const renderNetworkCard = (net: any, idx: number) => {
        const ipv4 = (net.ipv4 && net.ipv4.length > 0 && net.ipv4[0].address !== "0.0.0.0") 
            ? net.ipv4[0].address 
            : "No Valid IP";
        
        const ipv4Gateway = (net.ipv4 && net.ipv4.length > 0) ? net.ipv4[0].gateway : null;
        const linkStatus = net.link_status || "Unknown";

        return (
            <div key={idx} className="p-4 flex flex-col gap-2 relative group hover:bg-slate-50 dark:hover:bg-gray-800/40 transition-colors">
                <div className="flex justify-between items-start">
                    <div className="font-bold text-xs text-slate-900 dark:text-gray-200 truncate pr-2" title={net.name}>
                        {net.name || "Unknown Interface"}
                    </div>
                    {linkStatus === "LinkUp" ? (
                        <span className="shrink-0 w-2 h-2 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.8)]" title="Link Up"></span>
                    ) : (
                        <span className="shrink-0 w-2 h-2 rounded-full bg-slate-400 dark:bg-gray-600" title={linkStatus}></span>
                    )}
                </div>
                
                <div className="text-[10px] flex items-center justify-between mt-1 border-b border-slate-200 dark:border-gray-700/30 pb-1">
                    <span className="text-slate-500 dark:text-gray-400">MAC:</span>
                    <span className="font-mono text-slate-800 dark:text-gray-300">{net.mac || "-"}</span>
                </div>
                <div className="text-[10px] flex items-center justify-between border-b border-slate-200 dark:border-gray-700/30 pb-1">
                    <span className="text-slate-500 dark:text-gray-400">IPv4:</span>
                    <span className={`font-mono ${ipv4 === "No Valid IP" ? "text-slate-400 dark:text-gray-400" : "text-blue-600 dark:text-blue-400 font-semibold"}`}>
                        {ipv4}
                    </span>
                </div>
                <div className="text-[10px] flex items-center justify-between border-b border-slate-200 dark:border-gray-700/30 pb-1">
                    <span className="text-slate-500 dark:text-gray-400">Gateway:</span>
                    <span className={`font-mono ${!ipv4Gateway || ipv4Gateway === "0.0.0.0" ? "text-slate-400 dark:text-gray-400" : "text-slate-800 dark:text-gray-300"}`}>
                        {!ipv4Gateway || ipv4Gateway === "0.0.0.0" ? "-" : ipv4Gateway}
                    </span>
                </div>
                <div className="text-[10px] flex items-center justify-between border-b border-slate-200 dark:border-gray-700/30 pb-1">
                    <span className="text-slate-500 dark:text-gray-400">Speed:</span>
                    <span className="font-mono text-slate-800 dark:text-gray-300">
                        {net.speed_mbps ? `${net.speed_mbps} Mbps` : "-"}
                    </span>
                </div>
                <div className="text-[10px] flex items-center justify-between">
                    <span className="text-slate-500 dark:text-gray-400">Type:</span>
                    <span className="font-mono text-slate-800 dark:text-gray-300">{net.port_type || "-"}</span>
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center p-12 space-y-4 bg-white dark:bg-gray-900 rounded-xl border border-slate-200 dark:border-gray-800 shadow-sm">
                <div className="w-8 h-8 border-2 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin"></div>
                <p className="text-xs font-bold text-slate-600 dark:text-gray-200 uppercase tracking-widest">Loading iLO Data...</p>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="flex flex-col items-center justify-center p-12 bg-white dark:bg-gray-900 rounded-xl border border-slate-200 dark:border-gray-800 opacity-80 shadow-sm">
                <Server size={32} className="text-slate-400 dark:text-gray-200 mb-3" />
                <p className="text-xs font-bold text-slate-700 dark:text-gray-200 uppercase tracking-widest">No iLO data available</p>
                <p className="text-[10px] text-slate-500 dark:text-gray-400 mt-1">Ensure the BMC/iLO scanner has successfully run for this asset.</p>
            </div>
        );
    }

    const networks = safeParseJSON(data.ilo_network_interfaces);
    const dedicatedPorts = safeParseJSON(data.ilo_dedicated_network_ports);
    const sharedPorts = safeParseJSON(data.ilo_shared_network_ports);
    const tpms = safeParseJSON(data.tpm_info);
    const healthStyle = getHealthStyles(data.overall_health_status);

    return (
        <div className="flex flex-col gap-4 text-slate-900 dark:text-gray-200 px-2 pb-6">
            {/* Header / Summary row */}
            <div className="flex flex-wrap items-center gap-4 bg-white dark:bg-gray-800/40 p-4 rounded-xl border border-slate-200 dark:border-gray-700/50 shadow-sm dark:shadow-lg">
                <div className="flex items-center gap-2 pr-6 border-r border-slate-200 dark:border-gray-700/50">
                    <div className="p-1.5 bg-orange-600 rounded mr-1 shadow-sm">
                        <Server className="w-4 h-4 text-white" />
                    </div>
                    <div>
                        <h2 className="text-sm font-bold text-slate-900 dark:text-white leading-tight">iLO Overview</h2>
                        <p className="text-[10px] text-slate-500 dark:text-gray-400 font-mono mt-0.5">{data.target_host || "Unknown Host"}</p>
                    </div>
                </div>

                <div className="flex items-center gap-2 pr-6 border-r border-slate-200 dark:border-gray-700/50">
                    <span className="text-[10px] font-bold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Health:</span>
                    <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-bold uppercase tracking-wider border ${healthStyle}`}>
                        {data.overall_health_status === "CRITICAL" ? <AlertTriangle size={14} /> : 
                         data.overall_health_status === "WARNING" ? <AlertCircle size={14} /> : 
                         <CheckCircle2 size={14} />}
                        {data.overall_health_status || "UNKNOWN"}
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <span className="text-[10px] font-bold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Power:</span>
                    <div className="flex items-center gap-1.5">
                        <Power size={14} className={data.power_state === "On" ? "text-emerald-600 dark:text-emerald-400" : "text-slate-400 dark:text-gray-500"} />
                        <span className={`text-xs font-bold ${data.power_state === "On" ? "text-emerald-600 dark:text-emerald-400" : "text-slate-600 dark:text-gray-200"}`}>
                            {data.power_state || "Unknown"}
                        </span>
                    </div>
                </div>
            </div>

            {/* Main Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                
                {/* System Info Card */}
                <div className="bg-white dark:bg-gray-800/30 rounded-xl border border-slate-200 dark:border-gray-700/50 p-4 shadow-sm flex flex-col">
                    <h3 className="text-[11px] font-black text-slate-800 dark:text-gray-300 uppercase tracking-widest flex items-center gap-2 mb-4 border-b border-slate-200 dark:border-gray-700/50 pb-2">
                        <Cpu size={14} className="text-cyan-600 dark:text-cyan-400" /> System Details
                    </h3>
                    <div className="space-y-3 text-[11px]">
                        <div className="flex justify-between">
                            <span className="text-slate-500 dark:text-gray-400">Firmware:</span>
                            <span className="font-mono text-slate-800 dark:text-gray-300">{data.ilo_firmware || "-"}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-slate-500 dark:text-gray-400">Generation:</span>
                            <span className="text-slate-800 dark:text-gray-300 font-semibold">Gen {data.ilo_generation || "-"}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-slate-500 dark:text-gray-400">Manager Type:</span>
                            <span className="text-slate-800 dark:text-gray-300">{data.ilo_manager_type || "-"}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-slate-500 dark:text-gray-400">POST State:</span>
                            <span className="text-slate-800 dark:text-gray-300">{data.post_state || "-"}</span>
                        </div>
                        <div className="flex flex-col gap-1 pt-2 border-t border-slate-100 dark:border-gray-700/30">
                            <span className="text-slate-500 dark:text-gray-400">UUID:</span>
                            <span className="font-mono text-[10px] text-slate-700 dark:text-gray-300 break-all">{data.ilo_uuid || "-"}</span>
                        </div>
                    </div>
                </div>

                {/* Boot Configuration Card */}
                <div className="bg-white dark:bg-gray-800/30 rounded-xl border border-slate-200 dark:border-gray-700/50 p-4 shadow-sm flex flex-col">
                    <h3 className="text-[11px] font-black text-slate-800 dark:text-gray-300 uppercase tracking-widest flex items-center gap-2 mb-4 border-b border-slate-200 dark:border-gray-700/50 pb-2">
                        <HardDrive size={14} className="text-blue-600 dark:text-blue-400" /> Boot Configuration
                    </h3>
                    <div className="space-y-3 text-[11px]">
                        <div className="flex justify-between">
                            <span className="text-slate-500 dark:text-gray-400">Override Target:</span>
                            <span className="text-slate-800 dark:text-gray-300">{data.boot_source_override_target || "-"}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-slate-500 dark:text-gray-400">Override Enabled:</span>
                            <span className="text-slate-800 dark:text-gray-300">{data.boot_source_override_enabled || "-"}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-slate-500 dark:text-gray-400">Override Mode:</span>
                            <span className="text-slate-800 dark:text-gray-300">{data.boot_source_override_mode || "-"}</span>
                        </div>
                        <div className="flex justify-between pt-2 border-t border-slate-100 dark:border-gray-700/30">
                            <span className="text-slate-500 dark:text-gray-400">UEFI Target:</span>
                            <span className="text-slate-800 dark:text-gray-300">{data.uefi_target_boot_source || "-"}</span>
                        </div>
                    </div>
                </div>

                {/* Hardware Utilization Card */}
                <div className="bg-white dark:bg-gray-800/30 rounded-xl border border-slate-200 dark:border-gray-700/50 p-4 shadow-sm flex flex-col">
                    <h3 className="text-[11px] font-black text-slate-800 dark:text-gray-300 uppercase tracking-widest flex items-center gap-2 mb-4 border-b border-slate-200 dark:border-gray-700/50 pb-2">
                        <BarChart3 size={14} className="text-purple-600 dark:text-purple-400" /> Hardware Utilization
                    </h3>
                    <div className="space-y-3 text-[11px]">
                        <div className="flex justify-between">
                            <span className="text-slate-500 dark:text-gray-400">CPU Utilization:</span>
                            <span className="font-bold text-slate-800 dark:text-gray-300">{data.cpu_util_pct != null ? `${data.cpu_util_pct}%` : "-"}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-slate-500 dark:text-gray-400">Memory Bus:</span>
                            <span className="font-bold text-slate-800 dark:text-gray-300">{data.memory_bus_util_pct != null ? `${data.memory_bus_util_pct}%` : "-"}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-slate-500 dark:text-gray-400">I/O Bus:</span>
                            <span className="font-bold text-slate-800 dark:text-gray-300">{data.io_bus_util_pct != null ? `${data.io_bus_util_pct}%` : "-"}</span>
                        </div>
                    </div>
                </div>

                {/* Power & Thermal Card */}
                <div className="bg-white dark:bg-gray-800/30 rounded-xl border border-slate-200 dark:border-gray-700/50 p-4 shadow-sm flex flex-col">
                    <h3 className="text-[11px] font-black text-slate-800 dark:text-gray-300 uppercase tracking-widest flex items-center gap-2 mb-4 border-b border-slate-200 dark:border-gray-700/50 pb-2">
                        <Zap size={14} className="text-amber-500 dark:text-yellow-400" /> Power & Thermal
                    </h3>
                    <div className="space-y-3 text-[11px]">
                        <div className="flex justify-between">
                            <span className="text-slate-500 dark:text-gray-400">Consumed:</span>
                            <span className="font-bold text-amber-600 dark:text-yellow-400">{data.power_consumed_watts != null ? `${data.power_consumed_watts} Watts` : "-"}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-slate-500 dark:text-gray-400">Capacity:</span>
                            <span className="text-slate-800 dark:text-gray-300">{data.power_capacity_watts != null ? `${data.power_capacity_watts} Watts` : "-"}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-slate-500 dark:text-gray-400">Avg Consumed:</span>
                            <span className="text-slate-800 dark:text-gray-300">{data.power_average_consumed_watts != null ? `${data.power_average_consumed_watts} Watts` : "-"}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-slate-500 dark:text-gray-400">Regulator Mode:</span>
                            <span className="text-slate-800 dark:text-gray-300">{data.power_regulator_mode || "-"}</span>
                        </div>
                        <div className="flex justify-between pt-2 border-t border-slate-100 dark:border-gray-700/30">
                            <span className="text-slate-500 dark:text-gray-400 flex items-center gap-1"><Clock size={12}/> Uptime:</span>
                            <span className="font-mono text-slate-800 dark:text-gray-300">{formatUptime(data.uptime_seconds)}</span>
                        </div>
                    </div>
                </div>

                {/* Health Summary Card */}
                <div className="bg-white dark:bg-gray-800/30 rounded-xl border border-slate-200 dark:border-gray-700/50 p-4 shadow-sm flex flex-col">
                    <h3 className="text-[11px] font-black text-slate-800 dark:text-gray-300 uppercase tracking-widest flex items-center gap-2 mb-4 border-b border-slate-200 dark:border-gray-700/50 pb-2">
                        <Activity size={14} className="text-pink-600 dark:text-pink-400" /> Issues Summary
                    </h3>
                    
                    <div className="flex flex-col gap-3">
                        <div className="flex items-center justify-between p-2 rounded bg-rose-50/50 dark:bg-gray-900/50 border border-rose-100 dark:border-gray-700/50">
                            <span className="text-[11px] text-slate-700 dark:text-gray-200 flex items-center gap-2">
                                <AlertTriangle size={14} className="text-rose-600 dark:text-red-400" /> Critical
                            </span>
                            <span className="font-bold text-rose-600 dark:text-red-400">{data.critical_issues_count ?? 0}</span>
                        </div>
                        
                        <div className="flex items-center justify-between p-2 rounded bg-amber-50/50 dark:bg-gray-900/50 border border-amber-100 dark:border-gray-700/50">
                            <span className="text-[11px] text-slate-700 dark:text-gray-200 flex items-center gap-2">
                                <AlertCircle size={14} className="text-amber-600 dark:text-yellow-400" /> Warnings
                            </span>
                            <span className="font-bold text-amber-600 dark:text-yellow-400">{data.warnings_count ?? 0}</span>
                        </div>

                        <div className="flex items-center justify-between p-2 rounded bg-slate-50 dark:bg-gray-900/50 border border-slate-200 dark:border-gray-700/50">
                            <span className="text-[11px] text-slate-700 dark:text-gray-200 flex items-center gap-2">
                                <HelpCircle size={14} className="text-slate-500 dark:text-gray-400" /> Total Issues
                            </span>
                            <span className="font-bold text-slate-800 dark:text-gray-300">{data.total_issues_count ?? 0}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Network Section: Dedicated Ports */}
            {dedicatedPorts.length > 0 && (
                <div className="bg-white dark:bg-gray-800/30 rounded-xl border border-slate-200 dark:border-gray-700/50 shadow-sm overflow-hidden mt-2">
                    <div className="p-3 border-b border-slate-200 dark:border-gray-700/50 bg-slate-50 dark:bg-gray-900/40">
                        <h3 className="text-[11px] font-black text-slate-800 dark:text-gray-300 uppercase tracking-widest flex items-center gap-2">
                            <Link2 size={14} className="text-indigo-600 dark:text-indigo-400" /> Dedicated Network Ports
                        </h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 divide-y md:divide-y-0 md:divide-x divide-slate-200 dark:divide-gray-700/50">
                        {dedicatedPorts.map(renderNetworkCard)}
                    </div>
                </div>
            )}

            {/* Network Section: Shared Ports */}
            {sharedPorts.length > 0 && (
                <div className="bg-white dark:bg-gray-800/30 rounded-xl border border-slate-200 dark:border-gray-700/50 shadow-sm overflow-hidden mt-2">
                    <div className="p-3 border-b border-slate-200 dark:border-gray-700/50 bg-slate-50 dark:bg-gray-900/40">
                        <h3 className="text-[11px] font-black text-slate-800 dark:text-gray-300 uppercase tracking-widest flex items-center gap-2">
                            <Share2 size={14} className="text-orange-600 dark:text-orange-400" /> Shared Network Ports
                        </h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 divide-y md:divide-y-0 md:divide-x divide-slate-200 dark:divide-gray-700/50">
                        {sharedPorts.map(renderNetworkCard)}
                    </div>
                </div>
            )}

            {/* Fallback Network Interfaces (if specific arrays are empty) */}
            {dedicatedPorts.length === 0 && sharedPorts.length === 0 && (
                <div className="bg-white dark:bg-gray-800/30 rounded-xl border border-slate-200 dark:border-gray-700/50 shadow-sm overflow-hidden mt-2">
                    <div className="p-3 border-b border-slate-200 dark:border-gray-700/50 bg-slate-50 dark:bg-gray-900/40">
                        <h3 className="text-[11px] font-black text-slate-800 dark:text-gray-300 uppercase tracking-widest flex items-center gap-2">
                            <Network size={14} className="text-blue-600 dark:text-blue-400" /> Network Interfaces
                        </h3>
                    </div>
                    
                    {networks.length === 0 ? (
                        <div className="p-6 text-center text-[11px] text-slate-400 dark:text-gray-500">No network interfaces found.</div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 divide-y md:divide-y-0 md:divide-x divide-slate-200 dark:divide-gray-700/50">
                            {networks.map(renderNetworkCard)}
                        </div>
                    )}
                </div>
            )}

            {/* Security / TPM */}
            <div className="bg-white dark:bg-gray-800/30 rounded-xl border border-slate-200 dark:border-gray-700/50 shadow-sm overflow-hidden mt-2">
                <div className="p-3 border-b border-slate-200 dark:border-gray-700/50 bg-slate-50 dark:bg-gray-900/40">
                    <h3 className="text-[11px] font-black text-slate-800 dark:text-gray-300 uppercase tracking-widest flex items-center gap-2">
                        <ShieldCheck size={14} className="text-emerald-600 dark:text-emerald-400" /> TPM / Security Info
                    </h3>
                </div>
                
                {tpms.length === 0 ? (
                    <div className="p-6 text-center text-[11px] text-slate-400 dark:text-gray-500">No TPM information available.</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-[11px]">
                            <thead className="bg-slate-100 dark:bg-gray-900/50 border-b border-slate-200 dark:border-gray-700/50">
                                <tr>
                                    <th className="px-4 py-2 text-slate-700 dark:text-gray-200 font-medium">Vendor</th>
                                    <th className="px-4 py-2 text-slate-700 dark:text-gray-200 font-medium">Interface</th>
                                    <th className="px-4 py-2 text-slate-700 dark:text-gray-200 font-medium">Firmware</th>
                                    <th className="px-4 py-2 text-slate-700 dark:text-gray-200 font-medium">State</th>
                                    <th className="px-4 py-2 text-slate-700 dark:text-gray-200 font-medium">Health</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-200 dark:divide-gray-700/30">
                                {tpms.map((tpm: any, idx: number) => (
                                    <tr key={idx} className="hover:bg-slate-50 dark:hover:bg-gray-800/40 transition-colors">
                                        <td className="px-4 py-2 text-slate-800 dark:text-gray-300">{tpm.vendor || "-"}</td>
                                        <td className="px-4 py-2 text-slate-800 dark:text-gray-300">{tpm.interface_type || "-"}</td>
                                        <td className="px-4 py-2 text-slate-800 dark:text-gray-300 font-mono">{tpm.firmware_version || "-"}</td>
                                        <td className="px-4 py-2">
                                            <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${tpm.state === "Enabled" ? "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/20" : "bg-slate-100 dark:bg-gray-700 text-slate-600 dark:text-gray-200"}`}>
                                                {tpm.state || "Unknown"}
                                            </span>
                                        </td>
                                        <td className="px-4 py-2">
                                            <span className={`flex items-center gap-1 ${tpm.health === "OK" ? "text-emerald-600 dark:text-emerald-400" : "text-amber-600 dark:text-yellow-400"}`}>
                                                {tpm.health === "OK" ? <CheckCircle2 size={12} /> : <AlertTriangle size={12} />}
                                                {tpm.health || "Unknown"}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Footer Metadata */}
            <div className="flex justify-end gap-4 mt-2 px-2">
                <div className="text-[9px] text-slate-400 dark:text-gray-500 font-mono flex items-center gap-1">
                    <Globe size={10} /> Scan schema v{data.schema_version || "Unknown"}
                </div>
                <div className="text-[9px] text-slate-400 dark:text-gray-500 font-mono flex items-center gap-1">
                    <Clock size={10} /> Last Report: {data.report_generated_at ? new Date(data.report_generated_at + "Z").toLocaleString() : "-"}
                </div>
            </div>
        </div>
    );
}